import { APP_PERMISSIONS } from '@exam-center/permission';
import { useNavigate } from '@exam-center/router';
import { getBlockedRecordByActiveType } from '@exam-center/utils';
import { Flex, SectionTitle, styled } from '@shared/components';
import { useHasPermission } from '@shared/permissions';
import { dayjs } from '@shared/utils';
import { useEffect, useMemo, useState } from 'react';

import {
  BlockedRecordExamineeAccordion,
  BlockedRecordsEmptyState,
  BlockedRecordStaffAccordion,
  BlockRecordActiveTypeTabs,
  type BlockRecordActiveTypeTabValue,
} from '../../components';
import { useBlockedUserDetails } from '../../providers';

import BlockedRecordsActionButton from './blocked-records-action-button';
import { groupBlockedRecordsByBlockedType } from './utils';

const SectionWrapper = styled(Flex)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(4),
}));

export default function BlockedRecordsSection() {
  const hasUpdatePermission = useHasPermission(
    APP_PERMISSIONS.ADMIN_BLOCKLIST_MANAGEMENT.BLOCKED_USER.UPDATE,
  );
  const { blockedUserDetails } = useBlockedUserDetails();
  const navigate = useNavigate();

  const [blockStatusTab, setBlockStatusTab] = useState<BlockRecordActiveTypeTabValue>('active');
  const isActiveBlocks = blockStatusTab === 'active';
  const blockedRecords = useMemo(() => {
    const sortedBlockedRecord = [...blockedUserDetails.blockedRecords].sort((a, b) => {
      // If blockedUntil is null, put it at the end
      if (a.blockedUntil && !b.blockedUntil) return -1;
      if (!a.blockedUntil && b.blockedUntil) return 1;
      // If both have blockedUntil, sort by blockedUntil ascending
      if (a.blockedUntil && b.blockedUntil) {
        return dayjs(a.blockedUntil).isBefore(b.blockedUntil) ? -1 : 1;
      }
      // If both are null, sort by blockedAt ascending
      return dayjs(a.blockedAt).isBefore(b.blockedAt) ? -1 : 1;
    });

    const { activeRecords, pendingRecords, inactiveRecords } =
      getBlockedRecordByActiveType(sortedBlockedRecord);
    if (isActiveBlocks && (activeRecords.length > 0 || pendingRecords.length > 0)) {
      return [...activeRecords, ...pendingRecords];
    }
    return [...inactiveRecords];
  }, [blockedUserDetails, isActiveBlocks]);

  useEffect(() => {
    const sortedBlockedRecord = [...blockedUserDetails.blockedRecords].sort((a, b) => {
      if (a.blockedUntil && !b.blockedUntil) return -1;
      if (!a.blockedUntil && b.blockedUntil) return 1;
      if (a.blockedUntil && b.blockedUntil) {
        return dayjs(a.blockedUntil).isBefore(b.blockedUntil) ? -1 : 1;
      }
      return dayjs(a.blockedAt).isBefore(b.blockedAt) ? -1 : 1;
    });
    const { activeRecords, pendingRecords } = getBlockedRecordByActiveType(sortedBlockedRecord);
    if (isActiveBlocks && activeRecords.length === 0 && pendingRecords.length === 0) {
      setBlockStatusTab('in-active');
    }
  }, [blockedUserDetails, isActiveBlocks]);

  const { staffRecords, examPasserRecords, cheaterRecords } = useMemo(() => {
    return groupBlockedRecordsByBlockedType(blockedRecords);
  }, [blockedRecords]);

  const navigateToBlockedRecordDetails = (input: {
    blockedUserId: string;
    blockedRecordId: string;
  }) => {
    const { blockedUserId, blockedRecordId } = input;

    navigate('/admin/blocklist/:blockedUserId/:blockedRecordId', {
      params: { blockedUserId, blockedRecordId },
    });
  };

  const handleButtonClick = (input: { blockedUserId: string; blockedRecordId: string }) => {
    const { blockedUserId, blockedRecordId } = input;

    navigateToBlockedRecordDetails({
      blockedUserId,
      blockedRecordId,
    });
  };

  return (
    <Flex
      sx={(theme) => ({
        flexDirection: 'column',
        gap: theme.spacing(8),
        p: theme.spacing(6),
      })}
    >
      <BlockRecordActiveTypeTabs value={blockStatusTab} onChange={setBlockStatusTab} />
      <SectionWrapper>
        <SectionTitle title='ผู้ปฏิบัติหน้าที่' />
        {staffRecords.length === 0 ? (
          <BlockedRecordsEmptyState />
        ) : (
          staffRecords.map((staffRecord) => (
            <BlockedRecordStaffAccordion
              key={staffRecord.id}
              metadata={staffRecord.metadata}
              blockedType={staffRecord.blockedType}
              blockedScope={staffRecord.blockedScope}
              blockedUntil={staffRecord.blockedUntil}
              blockedAt={staffRecord.blockedAt}
              blockedReason={staffRecord.blockedReason}
              examCenterCode={staffRecord.examCenter?.code ?? null}
              examCenterName={staffRecord.examCenter?.nameTH ?? '-'}
              examCourseInfos={staffRecord.blockedRecordCourses
                .map((course) => ({
                  code: course.examCourse?.code ?? '',
                  name: course.examCourse?.name ?? '',
                }))
                .filter(
                  (info): info is { code: string; name: string } => !!info.code && !!info.name,
                )}
              endAdornment={
                <BlockedRecordsActionButton
                  variant={hasUpdatePermission ? 'edit' : 'view'}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleButtonClick({
                      blockedUserId: blockedUserDetails.id,
                      blockedRecordId: staffRecord.id,
                    });
                  }}
                />
              }
            />
          ))
        )}
      </SectionWrapper>
      <SectionWrapper>
        <SectionTitle title='ผู้ที่สอบผ่าน' />
        {examPasserRecords.length === 0 ? (
          <BlockedRecordsEmptyState />
        ) : (
          examPasserRecords.map((examPasserRecord) => (
            <BlockedRecordExamineeAccordion
              key={examPasserRecord.id}
              mode='exam-passer'
              blockedScope={examPasserRecord.blockedScope}
              blockedUntil={examPasserRecord.blockedUntil}
              blockedAt={examPasserRecord.blockedAt}
              blockedReason={examPasserRecord.blockedReason}
              examCenterCode={examPasserRecord.examCenter?.code ?? null}
              examCenterName={examPasserRecord.examCenter?.nameTH ?? '-'}
              examCourseInfos={examPasserRecord.blockedRecordCourses
                .map((course) => `${course.examCourse?.code}: ${course.examCourse?.name}`)
                .filter((code): code is string => !!code)}
              examRound={examPasserRecord.examEvent?.roundNumber ?? 0}
              examStartedAt={examPasserRecord.examEvent?.examStartedAt ?? new Date()}
              examEndedAt={examPasserRecord.examEvent?.examEndedAt ?? new Date()}
              endAdornment={
                isActiveBlocks ? (
                  <BlockedRecordsActionButton
                    variant={hasUpdatePermission ? 'edit' : 'view'}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleButtonClick({
                        blockedUserId: blockedUserDetails.id,
                        blockedRecordId: examPasserRecord.id,
                      });
                    }}
                  />
                ) : undefined
              }
            />
          ))
        )}
      </SectionWrapper>
      <SectionWrapper>
        <SectionTitle title='ผู้ที่ทุจริต' />
        {cheaterRecords.length === 0 ? (
          <BlockedRecordsEmptyState />
        ) : (
          cheaterRecords.map((cheaterRecord) => (
            <BlockedRecordExamineeAccordion
              key={cheaterRecord.id}
              mode='cheater'
              blockedScope={cheaterRecord.blockedScope}
              blockedUntil={cheaterRecord.blockedUntil}
              blockedAt={cheaterRecord.blockedAt}
              blockedReason={cheaterRecord.blockedReason}
              examCenterCode={cheaterRecord.examCenter?.code ?? null}
              examCenterName={cheaterRecord.examCenter?.nameTH ?? '-'}
              examCourseInfos={cheaterRecord.blockedRecordCourses
                .map((course) => `${course.examCourse?.code}: ${course.examCourse?.name}`)
                .filter((code): code is string => !!code)}
              examRound={cheaterRecord.metadata.examSession?.roundNumber ?? 0}
              examStartedAt={cheaterRecord.metadata.examSession?.startedAt ?? new Date()}
              examEndedAt={cheaterRecord.metadata.examSession?.endedAt ?? new Date()}
              endAdornment={
                <BlockedRecordsActionButton
                  variant={hasUpdatePermission ? 'edit' : 'view'}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleButtonClick({
                      blockedUserId: blockedUserDetails.id,
                      blockedRecordId: cheaterRecord.id,
                    });
                  }}
                />
              }
            />
          ))
        )}
      </SectionWrapper>
    </Flex>
  );
}
